import { Injectable } from '@angular/core';
import { OrderUpdateRequestStatus } from '@core/enums/order-update-request-status';
import { AdContentUpdate } from '@core/models/ad-content-update.model';
import { OrderAdDetailUpdate } from '@core/models/order-ad-detail-update.model';
import { OrderAdDetail } from '@core/models/order-ad-detail.model';
import { OrderContactDetails } from '@core/models/order-contact-details.model';
import { OrderUpdate } from '@core/models/order-update.model';
import { Order } from '@core/models/order.model';
import { Logger } from '@core/services/logger.service';
import { OrderViewModel } from '@order/models/order-view-model.model';
import { EditOrderConversionService } from './edit-order-conversion.service';
import { BackendOrderConverter } from '@order/services/backend-order-converter';

class createDisplayOrderUpdateRequestOptions {
	isOffer?: boolean;
	isAutoExportToExternalPlanner?: boolean;
	requestConfirmOrderAdDetails?: OrderAdDetail[];
}

@Injectable({
	providedIn: 'root'
})
export class OrderUpdateRequestService {

	private readonly COMPONENT_NAME = "OrderUpdateRequestService";

	constructor (
		private _logger: Logger,
		private _editOrderConversionService: EditOrderConversionService,
		private _backendOrderConverter: BackendOrderConverter) { }


	createRequest(orderViewModel: OrderViewModel, orderContactDetails: OrderContactDetails): OrderUpdate {
		const orderUpdate: OrderUpdate = new OrderUpdate();

		orderUpdate.id = orderViewModel.id;
		orderUpdate.verificationCode = "";
		orderUpdate.subscriberNumber = orderViewModel.subscriberNumber;

		// TBD: fill those in for non-login mode and if the contact details are different from default
		orderUpdate.contactDetails = orderContactDetails;
		// ...

		orderUpdate.orderAdDetailUpdates = [];

		for (const issueDate of orderViewModel.adVM.issueDates) {
			const orderAdDetailUpdate = new OrderAdDetailUpdate();
			orderAdDetailUpdate.id = ""; // new
			orderAdDetailUpdate.issueDate = this._editOrderConversionService.getIssueAsISOString(issueDate);
			orderAdDetailUpdate.packageId = orderViewModel.adVM.package?.id;
			orderAdDetailUpdate.placementId = orderViewModel.adVM.placement?.id;

			orderUpdate.orderAdDetailUpdates.push(orderAdDetailUpdate);
		}

		orderUpdate.adContentUpdates = [];

		const adContentUpdate = new AdContentUpdate();

		if (orderViewModel.contentVM.style) {
			adContentUpdate.styleId = orderViewModel.contentVM.style?.id;
			if (this._editOrderConversionService.isStyleMultifield(orderViewModel.contentVM.style)) {
				if (orderViewModel.contentVM.multiFieldEditorContent) {
					adContentUpdate.adContentElements = this._editOrderConversionService.createMultifieldContentElements(orderViewModel.contentVM.multiFieldEditorContent);
				}
				else {
					this._logger.error(this.COMPONENT_NAME, "createRequest(): the method is called when the multi-field content is empty. Look for a bug in the code.");
				}
			}
			else {
				adContentUpdate.adContentElements = this._editOrderConversionService.createSingleMultifieldContentElementsArray("maintext", orderViewModel.contentVM.adText, "");
			}
		}
		else {
			this._logger.error(this.COMPONENT_NAME, "createRequest(): the method is called when the Style is empty. Look for a bug in the code");
		}

		orderUpdate.adContentUpdates.push(adContentUpdate);

		return orderUpdate;
	}

	createDisplayOrderUpdateRequest(order: Order, options: createDisplayOrderUpdateRequestOptions | undefined = undefined): OrderUpdate {
		const orderUpdate: OrderUpdate = new OrderUpdate();

		orderUpdate.id = order.orderHeader.id || "";
		orderUpdate.advertiserId = order.orderHeader.advertiserId;
		orderUpdate.otherPayerId = order.orderHeader.otherPayerId;
		orderUpdate.campaignName = order.orderHeader.campaignName;
		orderUpdate.invoiceDescription = order.orderHeader.campaignName; // TODO will no longer be mandatory in the API (waiting for Prisa test system to be updated)
		orderUpdate.materialAgencyName = order.materialAgencyName;
		orderUpdate.materialContactFirstName = order.materialContactFirstName;
		orderUpdate.materialContactLastName = order.materialContactLastName;
		orderUpdate.materialContactEmail = order.materialContactEmail;
		orderUpdate.materialContactPhone = order.materialContactPhone;

		if (options) {
			if (options.isOffer) {
				orderUpdate.requestStatus = OrderUpdateRequestStatus.offer;
			}

			if (options.isAutoExportToExternalPlanner) {
				orderUpdate.externalPlanId = order.orderHeader.externalPlanId
			}
		}

		orderUpdate.orderAdDetailUpdates = order.orderAdDetails.map((orderAdDetail: OrderAdDetail) => {
			let orderAdDetailUpdate = this.createOrderAdDetailUpdate(orderAdDetail);
			if (options?.requestConfirmOrderAdDetails?.includes(orderAdDetail)) {
				orderAdDetailUpdate.requestStatus = "confirmed";
			}
			return orderAdDetailUpdate;
		});

		orderUpdate.orderServiceUpdates = this._backendOrderConverter.createOrderServiceUpdates(order.orderServices);

		return orderUpdate;
	}

	private createOrderAdDetailUpdate(orderAdDetail: OrderAdDetail): OrderAdDetailUpdate {
		const orderAdDetailUpdate = new OrderAdDetailUpdate();

		orderAdDetailUpdate.id = orderAdDetail.id || "";
		orderAdDetailUpdate.packageId = orderAdDetail.packageId;
		orderAdDetailUpdate.placementId = orderAdDetail.placementId;
		orderAdDetailUpdate.issueDate = orderAdDetail.issueDate;

		orderAdDetailUpdate.columns = orderAdDetail.columns;
		orderAdDetailUpdate.depth = orderAdDetail.depth;
		orderAdDetailUpdate.materialId = orderAdDetail.materialId;

		orderAdDetailUpdate.moduleCode = orderAdDetail.moduleCode ? orderAdDetail.moduleCode : undefined;
		orderAdDetailUpdate.placementRequest = orderAdDetail.placementRequest;
		orderAdDetailUpdate.confirmationNumber = orderAdDetail.confirmationNumber;

		return orderAdDetailUpdate;
	}
}
